import { useEffect, type RefObject } from 'react';
import { hasFinePointer } from './useReducedMotion';

export interface SpotlightOptions {
  /** Maximum tilt in degrees (capped at 3). 0 disables tilt. */
  tilt?: number;
}

/**
 * Cursor-follow glow + subtle tilt on fine pointers only. Writes `--mx/--my` (glow position)
 * and `--rx/--ry` (tilt) on the element and toggles `.is-lit`; pair with `.spotlight`,
 * `.spotlight-tilt` and a child `.spotlight-glow`.
 */
export function useSpotlight<T extends HTMLElement>(ref: RefObject<T | null>, { tilt = 3 }: SpotlightOptions = {}) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !hasFinePointer()) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const maxTilt = reduced ? 0 : Math.min(3, Math.max(0, tilt));

    let frame = 0;
    let px = 0;
    let py = 0;

    const apply = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      const x = px - rect.left;
      const y = py - rect.top;
      el.style.setProperty('--mx', `${x}px`);
      el.style.setProperty('--my', `${y}px`);
      if (maxTilt) {
        const nx = x / rect.width - 0.5;
        const ny = y / rect.height - 0.5;
        el.style.setProperty('--ry', `${(nx * 2 * maxTilt).toFixed(2)}deg`);
        el.style.setProperty('--rx', `${(-ny * 2 * maxTilt).toFixed(2)}deg`);
      }
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
      px = e.clientX;
      py = e.clientY;
      if (!frame) frame = requestAnimationFrame(apply);
    };
    const onEnter = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
      el.classList.add('is-lit');
      onMove(e);
    };
    const onLeave = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      el.classList.remove('is-lit');
      el.style.setProperty('--rx', '0deg');
      el.style.setProperty('--ry', '0deg');
    };

    el.addEventListener('pointerenter', onEnter);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref, tilt]);
}
